// src/components/shell/WorkspaceSwitcher.jsx
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import ActionMenu from '../common/ActionMenu';
import CreateWorkspaceModal from '../CreateWorkspaceModal';
import { ChevronDownIcon, PlusIcon } from './icons';

export function WorkspaceIcon({ workspace, small = false }) {
    const cls = `qd-ws-icon${small ? ' qd-ws-icon--sm' : ''}`;
    if (workspace?.iconUrl) {
        return <span className={cls}><img src={workspace.iconUrl} alt="" /></span>;
    }
    return <span className={cls}>{workspace?.name?.charAt(0)?.toUpperCase() ?? 'W'}</span>;
}

/**
 * The active workspace, and the way to change it — present on every page now,
 * not only in the file browser, so it's always clear whose files you're in.
 */
export default function WorkspaceSwitcher() {
    const {
        workspaces,
        activeWorkspace,
        setActiveWorkspace,
        addWorkspace,
        isLoading,
    } = useContext(WorkspaceContext);
    const [creating, setCreating] = useState(false);

    if (isLoading) {
        return (
            <div className="qd-ws-trigger is-loading" aria-busy="true">
                <span className="qd-ws-icon qd-ws-icon--placeholder" aria-hidden="true" />
                <span className="qd-ws-name">Loading workspaces…</span>
            </div>
        );
    }

    const items = [
        ...(workspaces || []).map((ws) => ({
            id: ws.id,
            label: ws.name,
            icon: <WorkspaceIcon workspace={ws} small />,
            checked: ws.id === activeWorkspace?.id,
            onSelect: () => setActiveWorkspace(ws),
        })),
        {
            id: 'new-workspace',
            label: 'New workspace',
            icon: <PlusIcon />,
            separatorBefore: true,
            onSelect: () => setCreating(true),
        },
    ];

    const name = activeWorkspace?.name ?? 'Select workspace';

    return (
        <>
            <ActionMenu
                className="qd-ws-switcher"
                label="Workspaces"
                align="start"
                items={items}
                focusOnOpen="checked"
                triggerClassName="qd-ws-trigger"
                triggerAriaLabel={`Workspace: ${name}`}
                header={<span className="qd-menu-caption">Workspaces</span>}
                trigger={(
                    <>
                        <WorkspaceIcon workspace={activeWorkspace} />
                        <span className="qd-ws-name">{name}</span>
                        <ChevronDownIcon className="qd-ws-chevron" size={14} />
                    </>
                )}
            />

            {/* Portaled: the top bar is its own stacking context, and a dialog
                trapped inside it would sit beneath the upload tray. */}
            {creating && createPortal(
                <CreateWorkspaceModal
                    onClose={() => setCreating(false)}
                    onCreated={(workspace) => {
                        addWorkspace(workspace);
                        setCreating(false);
                    }}
                />,
                document.body,
            )}
        </>
    );
}
